import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword as _fbSignIn, signOut as _fbSignOut, createUserWithEmailAndPassword as _fbCreateUser } from "firebase/auth";
import { getFirestore, doc as _fbDoc, getDoc as _fbGetDoc, setDoc as _fbSetDoc, updateDoc as _fbUpdateDoc, serverTimestamp as _fbServerTimestamp } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { getPerformance } from "firebase/performance";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Firebase configuration - NPI Governance Solutions
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "workplace-readiness-5ba15.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "workplace-readiness-5ba15",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "workplace-readiness-5ba15.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "454634611745",
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Development/Testing Mode Check with Security Validation
const isDevelopment = process.env.NODE_ENV === 'development';
const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// SECURITY: Prevent mock mode in production
if (process.env.NODE_ENV === 'production' && useMockData) {
  console.error('SECURITY ERROR: Mock authentication cannot be enabled in production!');
  throw new Error('Invalid configuration: Mock authentication disabled in production');
}

let auth, db, storage, messaging, analytics, secondaryAuth, functions;

// If in development and mock data is enabled, use mock implementation
if (isDevelopment && useMockData) {
  console.log('🔧 Using mock Firebase implementation for development');
  
  // Import mock data for development
  const { users, documents, attendance, modules } = require('./mock-data/index');

  // Initialize current user from localStorage for mock mode
  let _currentUser = null;
  try {
    const storedUser = localStorage.getItem('access70_lms_currentUser');
    if (storedUser) {
      _currentUser = JSON.parse(storedUser);
    }
  } catch (error) {
    console.error('Error loading stored user:', error);
  }

  const authStateListeners = [];

  const notifyAuthStateListeners = () => {
    authStateListeners.forEach(callback => callback(_currentUser));
  };

  // Mock Firebase Auth with SECURE password validation
  auth = {
    signInWithEmailAndPassword: async (email, password) => {
      // SECURITY FIX: Validate password even in mock mode
      if (!password || password.length < 6) {
        throw new Error('auth/weak-password');
      }
      
      const user = users.find(u => u.email === email);
      if (user && user.password && user.password === password) {
        _currentUser = user;
        localStorage.setItem('access70_lms_currentUser', JSON.stringify(user));
        notifyAuthStateListeners();
        return { user };
      } else {
        throw new Error('auth/user-not-found');
      }
    },
    createUserWithEmailAndPassword: async (email, password) => {
      const newUser = { uid: String(users.length + 1), email, role: 'learner', displayName: email };
      users.push(newUser);
      _currentUser = newUser;
      localStorage.setItem('access70_lms_currentUser', JSON.stringify(newUser));
      notifyAuthStateListeners();
      return { user: newUser };
    },
    signOut: () => {
      _currentUser = null;
      localStorage.removeItem('access70_lms_currentUser');
      notifyAuthStateListeners();
      return Promise.resolve();
    },
    onAuthStateChanged: (callback) => {
      authStateListeners.push(callback);
      callback(_currentUser);
      return () => {
        const index = authStateListeners.indexOf(callback);
        if (index > -1) {
          authStateListeners.splice(index, 1);
        }
      };
    },
    currentUser: _currentUser,
    // Add missing methods that Firebase auth expects
    _getRecaptchaConfig: () => Promise.resolve({}),
    useDeviceLanguage: () => {},
    languageCode: 'en'
  };

  // Mock Firestore implementation
  db = {
    collection: (path) => ({
      get: () => {
        let mockDocs = [];
        if (path === 'users') mockDocs = users.map(u => ({ id: u.uid, data: () => u }));
        if (path === 'modules') mockDocs = modules.map(m => ({ id: m.id, data: () => m }));
        if (path === 'attendance') mockDocs = Object.values(attendance).flat().map(a => ({ id: a.id, data: () => a }));

        return Promise.resolve({
          docs: mockDocs,
          forEach: (callback) => mockDocs.forEach(callback)
        });
      },
      where: (field, operator, value) => ({
        get: () => {
          let mockDocs = [];
          if (path === 'users' && field === 'role' && operator === '==') {
            const filteredUsers = users.filter(u => u.role === value);
            mockDocs = filteredUsers.map(u => ({ id: u.uid, data: () => u }));
          }
          if (path === 'attendance' && field === 'learnerId' && operator === '==') {
            mockDocs = (attendance[value] || []).map(a => ({ id: a.id, data: () => a }));
          }
          return Promise.resolve({
            docs: mockDocs,
            forEach: (callback) => mockDocs.forEach(callback)
          });
        },
      }),
      doc: (docId) => ({
        get: () => {
          if (path === 'users') {
            const user = users.find(u => u.uid === docId);
            return Promise.resolve({ exists: () => !!user, data: () => user });
          }
          return Promise.resolve({ exists: () => false });
        },
        collection: (subCollectionPath) => ({
          get: () => {
            if (subCollectionPath === 'documents') {
              const mockDocs = (documents[docId] || []).map(d => ({ id: d.id, data: () => d }));
              return Promise.resolve({
                docs: mockDocs,
                forEach: (callback) => mockDocs.forEach(callback)
              });
            }
            if (subCollectionPath === 'attendance') {
              const mockDocs = (attendance[docId] || []).map(a => ({ id: a.id, data: () => a }));
              return Promise.resolve({
                docs: mockDocs,
                forEach: (callback) => mockDocs.forEach(callback)
              });
            }
            return Promise.resolve({ docs: [] });
          },
          add: (data) => {
            if (subCollectionPath === 'documents') {
              if (!documents[docId]) documents[docId] = [];
              const newDoc = { id: String(documents[docId].length + 1), ...data };
              documents[docId].push(newDoc);
              return Promise.resolve({ id: newDoc.id });
            }
            return Promise.resolve();
          },
          doc: (subDocId) => ({
            update: (data) => {
              if (subCollectionPath === 'documents') {
                const docIndex = documents[docId].findIndex(d => d.id === subDocId);
                if (docIndex > -1) {
                  documents[docId][docIndex] = { ...documents[docId][docIndex], ...data };
                }
              }
              return Promise.resolve();
            },
          }),
        }),
        update: (data) => {
          if (path === 'users') {
            const userIndex = users.findIndex(u => u.uid === docId);
            if (userIndex > -1) {
              users[userIndex] = { ...users[userIndex], ...data };
            }
          }
          return Promise.resolve();
        },
        set: (data) => {
          if (path === 'users') {
            const userIndex = users.findIndex(u => u.uid === docId);
            if (userIndex > -1) {
              users[userIndex] = { ...users[userIndex], ...data };
            } else {
              users.push({ uid: docId, ...data });
            }
          }
          return Promise.resolve();
        },
      }),
      add: (data) => {
        const newId = String(Date.now());
        if (path === 'users') {
          users.push({ uid: newId, ...data });
        }
        return Promise.resolve({ id: newId });
      }
    }),
    doc: (path) => ({
      get: () => {
        if (path.startsWith('users/')) {
          const uid = path.split('/')[1];
          const user = users.find(u => u.uid === uid);
          return Promise.resolve({ exists: () => !!user, data: () => user });
        }
        return Promise.resolve({ exists: () => false });
      },
      update: (data) => {
        if (path.startsWith('users/')) {
          const uid = path.split('/')[1];
          const userIndex = users.findIndex(u => u.uid === uid);
          if (userIndex > -1) {
            users[userIndex] = { ...users[userIndex], ...data };
          }
        }
        return Promise.resolve();
      },
      set: (data) => {
        if (path.startsWith('users/')) {
          const uid = path.split('/')[1];
          const userIndex = users.findIndex(u => u.uid === uid);
          if (userIndex > -1) {
            users[userIndex] = { ...users[userIndex], ...data };
          } else {
            users.push({ uid, ...data });
          }
        }
        return Promise.resolve();
      }
    })
  };

  // Mock Storage implementation
  storage = {
    ref: (path) => ({
      put: (file) => ({
        on: (event, progress, error, complete) => {
          setTimeout(() => progress({ bytesTransferred: 50, totalBytes: 100 }), 50);
          setTimeout(() => progress({ bytesTransferred: 100, totalBytes: 100 }), 100);
          setTimeout(complete, 150);
        }
      }),
      getDownloadURL: () => Promise.resolve('https://mock-url.com/file.pdf')
    })
  };

  messaging = null;
  analytics = null;
  functions = null;
  secondaryAuth = auth; // In mock mode, use same auth instance

} else {
  console.log('🚀 Using production Firebase implementation');
  
  // Initialize Firebase
  const app = initializeApp(firebaseConfig);

  // Initialize Firebase services
  auth = getAuth(app);

  // Set auth language and configure
  auth.useDeviceLanguage();

  // Create secondary app for user creation without affecting current session
  const secondaryApp = initializeApp(firebaseConfig, 'secondary');
  secondaryAuth = getAuth(secondaryApp);
  secondaryAuth.useDeviceLanguage();
  
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app, 'us-central1'); // Explicitly set region

  // Set functions region to match deployment
  // Note: Explicitly setting region to us-central1 to match our deployment

  // Initialize optional services with feature detection
  messaging = null;
  analytics = null;

  // Initialize messaging if supported
  isMessagingSupported().then((supported) => {
    if (supported) {
      messaging = getMessaging(app);
    }
  }).catch(console.warn);

  // Initialize analytics if supported and measurement ID is provided
  if (process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) {
    isAnalyticsSupported().then((supported) => {
      if (supported) {
        analytics = getAnalytics(app);
        // Performance monitoring piggybacks on analytics — auto-traces network + page load
        if (process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true') {
          getPerformance(app);
        }
      }
    }).catch(console.warn);
  }
}

export { auth, db, storage, messaging, analytics, secondaryAuth, functions };

// ---------------------------------------------------------------------------
// Mock-compatible Firebase function wrappers
// Import from '../firebase' (this file) instead of 'firebase/auth' or
// 'firebase/firestore' so mock mode is handled transparently.
// ---------------------------------------------------------------------------
const _mock = isDevelopment && useMockData;

// Auth wrappers — static imports used so webpack resolves them correctly
export const signInWithEmailAndPassword = _mock
  ? (authInst, email, pass) => authInst.signInWithEmailAndPassword(email, pass)
  : _fbSignIn;

export const signOut = _mock
  ? (authInst) => authInst.signOut()
  : _fbSignOut;

export const createUserWithEmailAndPassword = _mock
  ? (authInst, email, pass) => authInst.createUserWithEmailAndPassword(email, pass)
  : _fbCreateUser;

// Firestore wrappers
export const doc = _mock
  ? (dbInst, ...segs) => dbInst.doc(segs.join('/'))
  : _fbDoc;

export const getDoc = _mock
  ? (docRef) => docRef.get()
  : _fbGetDoc;

export const setDoc = _mock
  ? (docRef, data) => docRef.set(data)
  : _fbSetDoc;

export const updateDoc = _mock
  ? (docRef, data) => docRef.update(data)
  : _fbUpdateDoc;

export const serverTimestamp = _mock
  ? () => new Date()
  : _fbServerTimestamp;

// Default export for compatibility
export default (isDevelopment && useMockData) ? {} : initializeApp(firebaseConfig);